import { useUser } from '@folklore/auth';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import Cookies from 'js-cookie';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { getImageAtSize } from '../../../lib/utils';

import Button from '../../buttons/Button';
import ExplanationModal from '../../modals/ExplanationModal';
import PincodeEntryModal from '../../modals/PincodeEntryModal';

import styles from '../../../../styles/themes/coorslive/play-coorslive.module.css';

interface PlayCoorsliveProps {
    page: PlayPage;
}

const PlayCoorslive = ({ page }: PlayCoorsliveProps) => {
    const {
        title,
        description = null,
        games = [],
        participations = null,
        blocks = null,
    } = page || {};
    const sortedGames = useMemo(
        () =>
            games.sort(
                (
                    { canParticipate: canParticipateA = false },
                    { canParticipate: canParticipateB = false },
                ) => {
                    if (canParticipateA === canParticipateB) {
                        return 0;
                    }
                    return canParticipateA ? -1 : 1;
                },
            ),
        [games],
    );
    const { unusedPincode = null } = useUser();
    const { drawsParticipations: pincodeDrawsParticipations = null } = unusedPincode || {};

    const [currentModal, setCurrentModal] = useState<{ blocks; url: string }>(null);

    const openPlayModal = useCallback(
        ({ blocks, url }) => setCurrentModal({ blocks, url }),
        [setCurrentModal],
    );
    const onModalClose = useCallback(() => {
        setCurrentModal(null);
    }, [setCurrentModal]);

    const { blocks: explanationModalBlocks = null, url: explanationModalUrl = null } =
        currentModal || {};

    const firstParticipation =
        participations !== null && participations.length > 0 ? participations[0] : null;

    const { gameId: firstGameId = null, ended: firstEnded = false } = firstParticipation || {};
    const pincodeEntryModalBlocks =
        blocks !== null
            ? blocks.filter(({ placement = 'header' }) => placement === 'pincode_entry_modal')
            : null;

    const [pincodeEntryModalOpened, setPincodeEntryModalOpened] = useState(
        pincodeDrawsParticipations !== null &&
            pincodeDrawsParticipations > 0 &&
            Cookies.get('pincode_entry_modal') !== 'closed',
    );
    const onClosePincodeEntryModal = useCallback(() => {
        setPincodeEntryModalOpened(false);
        Cookies.set('pincode_entry_modal', 'closed', { expires: 1 });
    }, []);

    return (
        <div className={styles.container}>
            <div className={classNames([styles.header, 'page-header'])}>
                <h1>{title}</h1>
                {description !== null ? <h2>{description}</h2> : null}
            </div>

            <div className={styles.games}>
                {sortedGames.map(
                    ({
                        id: gameId,
                        title: gameTitle,
                        headerImage = null,
                        image = null,
                        url: gameUrl,
                        canParticipate = false,
                        explanationModal = null,
                    }) => {
                        const disabled =
                            firstGameId !== null && firstGameId !== gameId && !firstEnded;
                        const closed = !canParticipate || disabled;

                        const { url: headerImageUrl = null, alt: headerImageAlt = null } =
                            headerImage !== null
                                ? getImageAtSize(headerImage, 'medium', false)
                                : {};

                        const { url: imageUrl = null, alt: imageAlt = null } =
                            image !== null ? getImageAtSize(image, 'medium', false) : {};

                        const hasExplanationModal =
                            explanationModal !== null &&
                            (firstGameId === null || firstGameId !== gameId);

                        return (
                            <div key={gameId} className={styles.game}>
                                {headerImageUrl !== null ? (
                                    <div className={styles.headerImage}>
                                        <img src={headerImageUrl} alt={headerImageAlt} />
                                    </div>
                                ) : (
                                    <h4>{gameTitle}</h4>
                                )}
                                {imageUrl !== null ? (
                                    <div className={styles.gameImage}>
                                        <img
                                            src={imageUrl}
                                            alt={imageAlt}
                                            className={styles.image}
                                        />{' '}
                                    </div>
                                ) : null}
                                <Button
                                    href={!hasExplanationModal ? gameUrl : null}
                                    onClick={
                                        hasExplanationModal
                                            ? () =>
                                                  openPlayModal({
                                                      blocks: explanationModal,
                                                      url: gameUrl,
                                                  })
                                            : null
                                    }
                                    className={styles.button}
                                    disabled={closed}
                                >
                                    <FormattedMessage
                                        defaultMessage="Play"
                                        description="PlayCoorslive"
                                    />
                                </Button>
                                {closed ? (
                                    <div className={styles.lockOverlay}>
                                        <div className={styles.pill}>
                                            <FontAwesomeIcon
                                                icon={faLock}
                                                className={styles.icon}
                                            />
                                            <span>
                                                <FormattedMessage
                                                    defaultMessage="Locked"
                                                    description="Play page button"
                                                />
                                            </span>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        );
                    },
                )}
            </div>

            <div className={styles.footer}>
                <FormattedMessage
                    defaultMessage="Come back every day to play more games for more chances to win!"
                    description="Coorslive Play"
                />
            </div>

            {explanationModalBlocks !== null && explanationModalUrl !== null ? (
                <ExplanationModal
                    blocks={explanationModalBlocks}
                    url={explanationModalUrl}
                    onClose={onModalClose}
                />
            ) : null}
            {pincodeEntryModalBlocks !== null &&
            pincodeEntryModalBlocks.length > 0 &&
            pincodeEntryModalOpened ? (
                <PincodeEntryModal
                    blocks={pincodeEntryModalBlocks}
                    quantity={pincodeDrawsParticipations}
                    onClose={onClosePincodeEntryModal}
                />
            ) : null}
        </div>
    );
};

export default PlayCoorslive;
